import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { MaterialGridInput } from 'models/order-input/shared';
import { PriceListMaterial } from 'models/material';
import { MaterialMacro } from 'models/material-macro';
import { ActionMaterial } from 'models/order-input/lab-order';
import MaterialMacroDialog from 'components/order-flow/lab-order/construction-step/construction-section/ations-and-materials/material-macro-dialog/material-macro-dialog.component';
import ActionMaterialDialog from 'components/order-flow/lab-order/construction-step/construction-section/ations-and-materials/action-material-dialog/action-material-dialog.component';
import {
    createMacroMaterialInputs,
    createActionMaterialInput,
} from 'components/order-flow/lab-order/construction-step/construction-section/ations-and-materials/actions-and-materials-helper';
import {
    ActionSelectionPayload,
    actionSelectionLabel,
} from 'components/order-flow/lab-order/construction-step/construction-section/ations-and-materials/actions-data-grid/action-selection';

type PostSaveMaterialPromptsProps = {
    // Actions saved in this session that have macros and/or actionMaterials to resolve.
    // Must be a stable reference - a new array restarts the walk.
    queue: ActionSelectionPayload[];
    priceListMaterials: PriceListMaterial[];
    // Called exactly once, when the whole queue has been walked, with everything chosen.
    onResolved: (materialInputs: MaterialGridInput[]) => void;
};

/**
 * Walks the queue one action at a time, offering the macro picker first and then the
 * existing single-material picker, mirroring the order used elsewhere. Materials are
 * accumulated locally and handed back in a single callback so the caller can persist
 * them with one mutation - writing per pick would race, since each write replaces the
 * whole addedMaterials array.
 */
const PostSaveMaterialPrompts: FC<PostSaveMaterialPromptsProps> = ({
    queue,
    priceListMaterials,
    onResolved,
}: PostSaveMaterialPromptsProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [macroResolved, setMacroResolved] = useState(false);
    const [collectedMaterials, setCollectedMaterials] = useState<MaterialGridInput[]>([]);
    const resolvedStepRef = useRef('');

    useEffect(() => {
        // A fresh queue restarts the walk.
        setCurrentIndex(0);
        setMacroResolved(false);
        setCollectedMaterials([]);
        resolvedStepRef.current = '';
    }, [queue]);

    const currentSelection: ActionSelectionPayload | undefined = queue[currentIndex];
    const hasMaterialMacros = Boolean(currentSelection?.materialMacroOptions.length);
    const hasActionMaterials = Boolean(currentSelection?.actionMaterials.length);
    const stepKey = `${currentIndex}:${macroResolved ? 'actionMaterial' : 'materialMacro'}`;

    // Both dialogs fire their add-callback and then onClose back to back, so a step must
    // only ever resolve once or the queue would skip ahead.
    const resolveStepOnce = (resolveStep: () => void) => {
        if (resolvedStepRef.current === stepKey) {
            return;
        }
        resolvedStepRef.current = stepKey;
        resolveStep();
    };

    const advance = (materialsFromThisStep: MaterialGridInput[]) => {
        const collected = [...collectedMaterials, ...materialsFromThisStep];
        const isLastSelection = currentIndex >= queue.length - 1;
        if (isLastSelection) {
            setCollectedMaterials([]);
            onResolved(collected);
            return;
        }
        setCollectedMaterials(collected);
        setCurrentIndex(currentIndex + 1);
        setMacroResolved(false);
    };

    const handleAddMaterialMacro = (materialMacro?: MaterialMacro) => {
        resolveStepOnce(() => {
            const materialInputs = materialMacro
                ? createMacroMaterialInputs(
                      materialMacro,
                      priceListMaterials,
                      currentSelection?.quantity || '1',
                      currentSelection?.actionId,
                  )
                : [];
            // The actionMaterial picker for this same action still has to run, if configured.
            if (hasActionMaterials) {
                setCollectedMaterials([...collectedMaterials, ...materialInputs]);
                setMacroResolved(true);
                return;
            }
            advance(materialInputs);
        });
    };

    const handleAddActionMaterial = (material?: ActionMaterial) => {
        resolveStepOnce(() =>
            advance(material ? [createActionMaterialInput(material, currentSelection?.quantity || '1')] : []),
        );
    };

    // "Add no material" routes through onClose, so an optional material must still
    // advance the queue. A required one stays non-dismissible.
    const handleSkipActionMaterial = () => {
        if (currentSelection?.isActionMaterialRequired) {
            return;
        }
        resolveStepOnce(() => advance([]));
    };

    if (!currentSelection) {
        return null;
    }

    const actionLabel = actionSelectionLabel(currentSelection);

    return (
        <Fragment>
            <MaterialMacroDialog
                open={hasMaterialMacros && !macroResolved}
                onClose={() => undefined}
                materialMacroOptions={currentSelection.materialMacroOptions}
                onAddMaterialMacro={handleAddMaterialMacro}
                actionLabel={actionLabel}
            />
            <ActionMaterialDialog
                open={hasActionMaterials && (!hasMaterialMacros || macroResolved)}
                onClose={handleSkipActionMaterial}
                actionMaterials={currentSelection.actionMaterials}
                onAddMaterial={handleAddActionMaterial}
                isMaterialRequired={currentSelection.isActionMaterialRequired}
                actionLabel={actionLabel}
            />
        </Fragment>
    );
};

export default PostSaveMaterialPrompts;
